/**
 * THE FIVE SCREENS. Every one is a pure function of its props.
 * ==========================================================
 *
 * No hooks, no fetches, no timers in this file. All of that lives in `WorkFlow.tsx`, which is the one
 * component here that holds state. Two things come out of that split:
 *
 *  · a test can render any screen in any state — including the states that are awkward to reach, like
 *    "offline and this KID has never been fetched" — without a DOM, a network stub or a clock;
 *  · a screen cannot start a request, so a screen cannot invent a reason for one failing.
 *
 * ── THE UX RULES THESE SCREENS ARE BUILT AROUND, each because its absence is a silent failure ───────
 *
 *  1. **A KID IS NEVER REQUIRED TO GET STARTED.** The owner requirement: technicians also survey
 *     *potential* locations, where no kiosk exists. So the home screen offers "no kiosk here yet" as a
 *     PEER of "work on a kiosk", not as a fallback, and the lookup screen carries "carry on without
 *     kiosk details" on every state including the failures. There is no path where a lookup failure
 *     stops work.
 *  2. **A PREFILLED VALUE IS NEVER PRESENTED AS THE TECHNICIAN'S OWN ANSWER.** That is enforced inside
 *     the form engine (`PrefillBadge`, `ReferenceNote`, and the review step's provenance tags), and
 *     these screens reinforce it: the kiosk summary is labelled with WHERE it came from — the server
 *     just now, or this session's memory — and the job-type screen says how many answers the record
 *     will supply versus how many the technician will be asked.
 *  3. **GLOVES, DAYLIGHT, ONE HAND.** Primary actions are full-width `fx-big` buttons (≥64px, they
 *     inherit the 44px floor and then some), the tap target is the whole card rather than a link inside
 *     it, and nothing is revealed on hover. State is carried by text and by a border, never by colour
 *     alone.
 */

import { accountFor, allFields, getSchema, SCHEMAS } from '@/forms';
import type { FormSchema, SubmissionPayload } from '@/forms';
import type { SessionRunwayWarning } from '@/auth/phase';
import type { ApiOutcome, InterventionListItem, InterventionListPage } from '@/core/api';
import type { OutboxCounts } from '@/core/outbox';
import { ApiNotice } from './ApiNotice';
import { eligibleSchemas } from './form-eligibility';
import { FormHost } from './FormHost';
import type { JobDispatch, KioskContext } from './flow';
import { isWellFormedKid, type LookupResult } from './kiosk';

/* -------------------------------------------------------------------- home ---- */

export interface HomeScreenProps {
  readonly counts: OutboxCounts;
  /** The work list. FIRST, because PRD §4.3 makes the work order the entry point, not the number. */
  readonly onOpenJobs: () => void;
  readonly onWorkOnKiosk: () => void;
  readonly onNoKiosk: () => void;
}

export function HomeScreen({
  counts,
  onOpenJobs,
  onWorkOnKiosk,
  onNoKiosk,
}: HomeScreenProps): JSX.Element {
  const pending = counts.queued + counts.inFlight;
  return (
    <section className="fx-screen" aria-label="Start work">
      <h2 className="section-title">Start work</h2>

      {/*
        FIRST on the screen, and that ordering is the requirement rather than a preference. PRD §4.3,
        from field feedback on the demo: *"The KID is not on the locker (Cherokee Baldwin), so barcode
        scanning of the KID is out and the entry point is the work order, not the number: 'here are your
        assigned jobs → pick one → the kiosk comes with it', with manual KID entry demoted to a
        fallback."*

        It is also the only path on which the four values the owner named — case, location, kiosk id,
        installation id — can be prefilled at all, because they live on the intervention.
      */}
      <button type="button" className="fx-big" onClick={onOpenJobs}>
        <span className="fx-big__title">My assigned jobs</span>
        <span className="fx-big__note">
          Pick a job and the kiosk, the location and the case come with it. Needs signal.
        </span>
      </button>

      <button type="button" className="fx-big" onClick={onWorkOnKiosk}>
        <span className="fx-big__title">Work on a kiosk</span>
        <span className="fx-big__note">
          Look it up by KID if you have one. You can also carry on without it.
        </span>
      </button>

      {/*
        A peer of the button above, not a "no thanks" link under it. Surveying a location with nothing
        installed is real work with its own forms, and the entry point has to admit that exists —
        otherwise the only way in is to invent a KID.
      */}
      <button type="button" className="fx-big" onClick={onNoKiosk}>
        <span className="fx-big__title">No kiosk here yet</span>
        <span className="fx-big__note">
          Surveying a possible location, or a site with nothing installed. No KID needed.
        </span>
      </button>

      <p className="fx-help">
        You do not need a KID to start, and the two lower paths need no signal either.
      </p>

      {pending > 0 && (
        <p className="muted">
          {pending} {pending === 1 ? 'submission is' : 'submissions are'} saved on this phone and waiting
          to send. Open <strong>Outbox</strong> below to see them.
        </p>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ lookup ---- */

export interface KioskLookupScreenProps {
  readonly draft: string;
  readonly onDraft: (value: string) => void;
  readonly onLookup: () => void;
  readonly busy: boolean;
  readonly result: LookupResult | null;
  /** Continue with whatever we resolved. */
  readonly onUse: (kiosk: KioskContext) => void;
  /** Continue with no kiosk details at all — always available. */
  readonly onSkip: () => void;
  readonly onBack: () => void;
}

export function KioskLookupScreen(props: KioskLookupScreenProps): JSX.Element {
  const { draft, onDraft, onLookup, busy, result, onUse, onSkip, onBack } = props;
  const trimmed = draft.replace(/[\s-]+/g, '');
  // The shared client/server rule: 6–10 digits (see `isWellFormedKid` for the data census).
  const wellFormed = isWellFormedKid(trimmed);

  return (
    <section className="fx-screen" aria-label="Find the kiosk">
      <h2 className="section-title">Which kiosk?</h2>

      <label className="fx-label" htmlFor="kid">
        Kiosk ID (KID)
      </label>
      <p id="kid-help" className="fx-help">
        6 to 10 digits, from your dispatch. It is not printed on the locker.
      </p>
      {/*
        `aria-label="Kiosk ID"` was here as well as the `<label>` above. An `aria-label` REPLACES the
        associated label, so the accessible name was "Kiosk ID" while the visible name read "Kiosk ID
        (KID)" — a mismatch that breaks voice control (WCAG 2.5.3: the accessible name must contain the
        visible text, not a truncation of it) and hid the abbreviation the dispatch actually uses. The
        `<label htmlFor>` is correct on its own, so the override is gone.

        `aria-describedby` now also points at the character counter, so the count is part of the field's
        description rather than a paragraph a screen-reader user never reaches.
      */}
      <input
        id="kid"
        className="fx-kid"
        aria-describedby="kid-help kid-count"
        inputMode="numeric"
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        enterKeyHint="search"
        value={draft}
        onChange={(event) => onDraft(event.target.value)}
      />

      {/*
        Rendered unconditionally and as a polite live region: the counter is the only feedback that a
        KID is the wrong shape, and the "Look up this kiosk" button silently disables itself until the
        KID is well-formed (6–10 digits). A technician who cannot see why the button is dead is a
        technician who decides the app is broken. Mounted empty rather than conditionally inserted, for
        the same reason as the outbox's status line.
      */}
      <p id="kid-count" className="fx-help" role="status" aria-live="polite">
        {trimmed.length === 0
          ? 'Type the KID — 6 to 10 digits — to enable the lookup.'
          : !/^[0-9]+$/.test(trimmed)
            ? 'A KID is digits only — remove the letters or symbols to enable the lookup.'
            : trimmed.length < 6
              ? `${trimmed.length} of at least 6 digits — the lookup stays off until at least 6 are in.`
              : trimmed.length > 10
                ? `${trimmed.length} digits — a KID is at most 10, so check for an extra digit.`
                : `${trimmed.length} digits entered — ready to look up.`}
      </p>

      <div className="fx-row">
        <button
          type="button"
          className="fx-big"
          disabled={busy || !wellFormed}
          onClick={onLookup}
        >
          {busy ? 'Looking it up…' : 'Look up this kiosk'}
        </button>
      </div>

      {result !== null && <LookupOutcome result={result} onUse={onUse} />}

      {/*
        Present on EVERY state of this screen, including before a lookup and after a failure. A
        technician standing at a locker with no signal must be able to start the job; making the
        lookup a gate would turn a missing kiosk record into a missing day's work.
      */}
      <button type="button" className="fx-big fx-big--quiet" onClick={onSkip}>
        <span className="fx-big__title">Carry on without kiosk details</span>
        <span className="fx-big__note">
          {wellFormed
            ? `The KID ${trimmed.toUpperCase()} is recorded with the submission. Questions the record would have answered are asked instead.`
            : 'Nothing from the record is filled in, so you are asked those questions instead.'}
        </span>
      </button>

      <button type="button" className="button" onClick={onBack}>
        Back
      </button>
    </section>
  );
}

function LookupOutcome({
  result,
  onUse,
}: {
  readonly result: LookupResult;
  readonly onUse: (kiosk: KioskContext) => void;
}): JSX.Element {
  if (result.kind === 'found') {
    const payload = result.payload;
    return (
      <article className="card" data-lookup="found">
        <div className="card__row">
          <strong>{payload.name ?? payload.kioskId}</strong>
          {/*
            `state--plain`, not `state--synced`. A KID is an identifier, not a state — showing it in the
            green "sent" chip borrowed a success colour for a fact that is neither good nor bad news, and
            put the same green next to a kiosk record whose `status.state` might be anything at all.
          */}
          <span className="state state--plain">{payload.kioskId}</span>
        </div>
        {/*
          Provenance, not decoration. "The server said this a moment ago" and "this is what the server
          said earlier in this session, and we cannot check it now" are different claims.
        */}
        <p className="muted">
          {result.from === 'server'
            ? `From the record, read just now (${payload._meta.retrievedAt}).`
            : 'From earlier in this session — the phone could not reach the server to re-check it. It may have changed.'}
        </p>
        <dl className="fx-kv">
          <KioskFact label="Status" value={payload.status?.state} />
          <KioskFact label="Site" value={payload.location?.['siteName']} />
          <KioskFact label="Street" value={payload.location?.['street']} />
          <KioskFact label="Generation" value={payload.hardware?.['generation']} />
          <KioskFact label="Layout" value={payload.hardware?.['layoutString']} />
          <KioskFact label="Case" value={payload.caseNumber} />
        </dl>
        {payload.hours !== undefined && (
          <p className="muted">
            Store hours are on the record but are <strong>unverified</strong>: {payload.hours.note}
          </p>
        )}
        {payload.recovery !== undefined && payload.recovery.flagged && (
          <p className="banner banner--danger" role="alert">
            {payload.recovery.advice}
          </p>
        )}
        <p className="muted">
          {payload._meta.omittedEmptyFields} field(s) were left out of this payload because the record
          holds nothing for them. Blank here means blank on the record, not blank because of an error.
        </p>
        <button
          type="button"
          className="fx-big"
          onClick={() => onUse({ kioskId: payload.kioskId, payload, source: result.from })}
        >
          Use this kiosk
        </button>
      </article>
    );
  }

  if (result.kind === 'unseen-offline') {
    /**
     * The state the brief singles out. It is NOT rendered as an error, because nothing failed: the
     * device deliberately keeps no kiosk records on disk (a stored kiosk payload is another supplier's
     * data with no ownership check in front of it — C-04), so offline there is genuinely nothing to
     * look up.
     */
    return (
      <div className="banner banner--offline" role="status" data-lookup="unseen-offline">
        <p className="banner__title">Offline, and this phone has not seen {result.kioskId} before</p>
        <p className="banner__body">
          Nothing has gone wrong. This app does not keep kiosk records on the phone on purpose, so with
          no signal there is nothing to look up. Carry on below — the KID is recorded with your
          submission, and the questions the record would have answered are asked instead.
        </p>
      </div>
    );
  }

  return <ApiNotice outcome={result.outcome} what="look up that kiosk" />;
}

function KioskFact({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string | number | undefined;
}): JSX.Element | null {
  if (value === undefined || value === '') return null;
  return (
    <div>
      <dt>{label}</dt>
      <dd>{String(value)}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------- jobs ---- */

/**
 * Does a job row match a search query? (H6 — field feedback 6.)
 *
 * Case-insensitive substring over the four identifiers a technician would actually type off a
 * dispatch: the location name, the kiosk id, the case number and the installation id. Exported and
 * pure, so a test asserts the matching itself and not a rendering side effect. A blank query matches
 * everything — the unfiltered list is the `''` case, not a separate code path.
 */
export function matchesJobSearch(item: InterventionListItem, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === '') return true;
  return [item.locationName, item.kioskId, item.caseNumber, item.installationId].some(
    (value) => value !== null && value.toLowerCase().includes(q),
  );
}

export interface JobListScreenProps {
  readonly busy: boolean;
  /** `null` before the first attempt. Pure prop, so every state is renderable in a test. */
  readonly outcome: ApiOutcome<InterventionListPage> | null;
  /**
   * H6 — the search box's value. State lives in `WorkFlow.tsx` and arrives as a prop because this
   * file is hook-free; the screen only filters and renders.
   */
  readonly search: string;
  readonly onSearch: (query: string) => void;
  /**
   * D5 — the 10-hour session warning, at the moment it matters. `null` (or absent) almost always:
   * `describeSessionRunway` in `src/auth/phase.ts` returns a value only when signed in AND the
   * refresh window is nearly out, so this screen renders it without re-deciding the condition.
   * Pure prop, same reason as `outcome`.
   */
  readonly sessionRunway?: SessionRunwayWarning | null;
  readonly onRetry: () => void;
  readonly onPick: (item: InterventionListItem) => void;
  readonly onBack: () => void;
}

/**
 * The work list. *"Here are your assigned jobs → pick one → the kiosk comes with it"* (PRD §4.3).
 *
 * Three properties are worth naming because each one's absence is a failure this app exists to
 * remove:
 *
 *  1. **A 503 is rendered as a 503, never as an empty list.** `ApiNotice` keeps the outcome variants
 *     apart, and the route answers `503` with a named blocker precisely so *"the integration is
 *     down"* cannot be read as *"you have no work today"*. An empty list, when the server really
 *     does return zero rows, gets its own explicit message saying which of the two it is.
 *  2. **`Completed` rows are SHOWN, and shown as not startable.** `actionable: false` arrives on the
 *     row; hiding those jobs would look to a technician like their submitted work vanished. The
 *     server's own comment says so and this screen is the half that has to honour it.
 *  3. **The recovery warning is rendered before travel, not after.** `recovery.flagged` means the
 *     kiosk came back into service after the job was raised — a 90-minute drive to a working locker.
 *     `_meta.droppedByStateFilter` is surfaced too: non-zero means the server's own query and its own
 *     classifier disagreed, and a technician seeing four jobs where the count said six deserves the
 *     explanation rather than the mystery.
 */
export function JobListScreen(props: JobListScreenProps): JSX.Element {
  const { busy, outcome, search, onSearch, sessionRunway, onRetry, onPick, onBack } = props;

  /*
   * H6 — plain consts, not hooks (this file is hook-free by contract). `page` narrows the ok
   * branch once instead of re-testing `outcome.kind` in every JSX guard below.
   */
  const page = outcome !== null && outcome.kind === 'ok' ? outcome.data : null;
  const query = search.trim();
  const shown = page === null ? [] : page.items.filter((item) => matchesJobSearch(item, search));

  return (
    <section className="fx-screen" aria-label="My assigned jobs">
      <h2 className="section-title">My assigned jobs</h2>

      {/*
        D5, and the same principle as the recovery warning below: the warning worth rendering is the
        one that arrives BEFORE travel. It sits above the list because the decision it informs is
        "which job, and do I sign in again first" — after a job is picked it is too late to be useful.
        `role="status"`, not `alert`: nothing has failed yet, which is the whole point of saying it now.
      */}
      {sessionRunway != null && (
        <div className={`banner banner--${sessionRunway.tone}`} role="status" data-runway="short">
          <p className="banner__title">{sessionRunway.title}</p>
          <p className="banner__body">{sessionRunway.body}</p>
        </div>
      )}

      {busy && <p className="fx-help">Reading your work list…</p>}

      {outcome !== null && outcome.kind !== 'ok' && (
        <ApiNotice outcome={outcome} what="read your work list" />
      )}

      {page !== null && page.items.length === 0 && (
        <div className="banner banner--ok" role="status">
          <p className="banner__title">No jobs assigned to you right now</p>
          <p className="banner__body">
            This is the server answering, not a failure — it returned a list and the list was empty.
            You can still start work from a KID or with no kiosk at all.
          </p>
        </div>
      )}

      {/*
        H6 — the search box (field feedback 6). Rendered only when there is a list to search; a
        fixed id is fine because exactly one job list is ever mounted (unlike the scanner sheets,
        which taught this codebase the useId lesson). The status line under it is the same pattern
        as the KID counter: mounted whenever the box is, polite, and it is where the CLIENT-SIDE
        SCOPE is stated — this searches the loaded page, not the server.
      */}
      {page !== null && page.items.length > 0 && (
        <>
          <label className="fx-label" htmlFor="job-search">
            Search this list
          </label>
          <input
            id="job-search"
            type="search"
            className="fx-search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Location, kiosk, case or installation"
            aria-describedby="job-search-scope"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
          />
          <p id="job-search-scope" className="fx-help" role="status" aria-live="polite">
            {query === ''
              ? 'Matches the location, kiosk, case or installation of the jobs on this page.'
              : `Showing ${shown.length} of ${page.items.length} job${page.items.length === 1 ? '' : 's'} on this page.`}
          </p>
        </>
      )}

      {/*
        Filtered to nothing is NOT "no work" (backlog constraint 1). The completed-jobs guarantee —
        a technician can always see their submitted work did not vanish — must survive the filter,
        so an empty match says exactly what happened and hands back the whole list in one tap.
        `banner--ok` + `role="status"`, matching the genuine empty list above: nothing has failed.
      */}
      {page !== null && page.items.length > 0 && query !== '' && shown.length === 0 && (
        <div className="banner banner--ok" role="status">
          <p className="banner__title">No jobs match your search</p>
          <p className="banner__body">
            All {page.items.length} job{page.items.length === 1 ? '' : 's'} on this page {page.items.length === 1 ? 'is' : 'are'} still
            assigned to you — nothing was removed. The search just found none of them
            {page.nextCursor !== null ? ', and it only checks the jobs already loaded on this page' : ''}.
          </p>
          <button type="button" className="button" onClick={() => onSearch('')}>
            Clear the search
          </button>
        </div>
      )}

      {shown.map((item) => (
        <JobRow key={item.interventionId} item={item} onPick={onPick} />
      ))}

      {page !== null && page._meta.droppedByStateFilter > 0 && (
        <p className="banner banner--danger" role="alert">
          {page._meta.droppedByStateFilter} job(s) the server sent were dropped by its own
          state filter. That means its query and its classifier disagree. Your list may be short —
          please report this.
        </p>
      )}

      {page !== null && page.nextCursor !== null && (
        <p className="fx-help">
          There are more jobs than this page shows.
          {query !== '' &&
            ' Your search only checks the jobs loaded on this page — a job further down would not be found here.'}
        </p>
      )}

      <button type="button" className="button" disabled={busy} onClick={onRetry}>
        {outcome === null ? 'Load my jobs' : 'Check again'}
      </button>
      <button type="button" className="button" onClick={onBack}>
        Back
      </button>
    </section>
  );
}

function JobRow({
  item,
  onPick,
}: {
  readonly item: InterventionListItem;
  readonly onPick: (item: InterventionListItem) => void;
}): JSX.Element {
  /*
   * `Completed` arrives as `actionable: false`. Rendered as a card with no start button rather than
   * omitted: it is the SUPPLIER's assertion that the work is done and Amazon has not accepted it
   * yet, and a technician who cannot see it concludes their submission was lost.
   */
  const body = (
    <>
      <span className="fx-big__title">{item.locationName ?? item.name ?? item.interventionId}</span>
      <span className="fx-big__note">
        {[item.type, item.subType, item.vendorState].filter((x) => x !== null).join(' · ')}
      </span>
      <span className="fx-big__note">
        {[
          item.kioskId === null ? 'no kiosk on this job' : `kiosk ${item.kioskId}`,
          item.caseNumber === null ? null : `case ${item.caseNumber}`,
          item.installationId === null ? null : `installation ${item.installationId}`,
          item.scheduledDate === null ? null : `scheduled ${item.scheduledDate}`,
          item.rescheduleCount === undefined ? null : `rescheduled ${item.rescheduleCount}×`,
        ]
          .filter((x) => x !== null)
          .join(' · ')}
      </span>
      {item.recovery.flagged && (
        <span className="fx-big__warn" role="alert">
          {item.recovery.advice}
        </span>
      )}
    </>
  );

  if (!item.actionable) {
    return (
      <article className="card" data-job="read-only">
        {body}
        <p className="muted">
          You have already reported this one as complete. It is waiting for Amazon to accept it, so
          there is nothing to submit — it is here so you can see it was not lost.
        </p>
      </article>
    );
  }

  return (
    <button type="button" className="fx-big" onClick={() => onPick(item)}>
      {body}
    </button>
  );
}

/* --------------------------------------------------------------- job types ---- */

/**
 * Does this form expect a kiosk record to exist?
 *
 * Answered from the schema's own data — whether any field binds to a `kiosk.*` prefill source — rather
 * than from a hardcoded list of form ids. A hardcoded list is a second place to update when a schema
 * changes, and the one that gets forgotten.
 */
export function expectsKiosk(schema: FormSchema): boolean {
  return [...allFields(schema)].some(({ field }) => field.prefill?.source.startsWith('kiosk.') === true);
}

export interface JobTypeScreenProps {
  readonly kiosk: KioskContext;
  readonly dispatch?: JobDispatch | undefined;
  readonly onPick: (schemaId: string) => void;
  readonly onBack: () => void;
}

export function JobTypeScreen({ kiosk, dispatch, onPick, onBack }: JobTypeScreenProps): JSX.Element {
  /*
   * Only the forms this dispatch is for (field feedback 3). The type→forms table lives in
   * `form-eligibility.ts` where it is documented as an owner-reviewable list; an unmapped or
   * absent type — and the no-dispatch typed-KID path — falls back to ALL forms, so a technician
   * is never locked out of the form they actually need. Pure function of props, like everything
   * else on this screen.
   */
  const offered = eligibleSchemas(SCHEMAS, dispatch);
  const narrowed = offered.length < SCHEMAS.length;
  return (
    <section className="fx-screen" aria-label="What kind of job">
      <h2 className="section-title">What are you doing?</h2>
      {dispatch !== undefined && (
        // Said before the forms are listed, because the per-form counts below are counted over the
        // schema and do NOT know whether the dispatch happens to fill them. Naming the job is how a
        // technician can tell "this form knows my case number" from "I am about to retype it".
        <p className="fx-help">
          {[
            dispatch.intervention.type,
            dispatch.intervention.locationName,
            dispatch.intervention.installationId === null
              ? null
              : `installation ${dispatch.intervention.installationId}`,
          ]
            .filter((x) => x !== null && x !== undefined)
            .join(' — ')}
        </p>
      )}
      <p className="fx-help">
        {kiosk.kioskId === null
          ? 'No kiosk. Forms that would normally read the kiosk record will ask you those questions instead.'
          : `Kiosk ${kiosk.kioskId}${
              kiosk.payload === null
                ? ' — no record on this phone, so nothing is filled in for you.'
                : kiosk.source === 'session'
                  ? ' — using details read earlier in this session.'
                  : ' — using details from the record.'
            }`}
      </p>

      {narrowed && (
        // Said out loud rather than silently narrowing: a technician who expected a form that is
        // not listed needs to know it was filtered on the dispatch type, not removed from the app.
        <p className="fx-help">
          Showing the form(s) for a {dispatch?.intervention.type} job. If the form you need is not
          here, the dispatch type is wrong — report it.
        </p>
      )}

      {offered.map((schema) => {
        const accounting = accountFor(schema);
        const needsKiosk = expectsKiosk(schema);
        const noRecord = kiosk.payload === null;
        return (
          <button key={schema.id} type="button" className="fx-big" onClick={() => onPick(schema.id)}>
            <span className="fx-big__title">{schema.title}</span>
            <span className="fx-big__note">
              {accounting.total} question(s): {accounting.asked} asked of you,{' '}
              {accounting.prefilled} from the record where it is trusted
              {accounting.reference > 0 ? `, ${accounting.reference} shown only to check against` : ''}.
            </span>
            {schema.source.provenance !== 'sourced' && (
              <span className="fx-big__note">
                Parts of this form are reconstructed rather than taken from an existing form. Fields
                marked <em>inferred</em> are under review.
              </span>
            )}
            {needsKiosk && noRecord && (
              <span className="fx-big__warn">
                This form normally reads the kiosk record. Without it you will be asked those{' '}
                {accounting.prefilled} question(s) yourself. That is allowed.
              </span>
            )}
          </button>
        );
      })}

      <button type="button" className="button" onClick={onBack}>
        Back
      </button>
    </section>
  );
}

/* -------------------------------------------------------------------- form ---- */

export interface JobFormScreenProps {
  readonly kiosk: KioskContext;
  readonly schemaId: string;
  readonly dispatch?: JobDispatch | undefined;
  readonly submit: (payload: SubmissionPayload) => Promise<void>;
  readonly onBack: () => void;
}

export function JobFormScreen({
  kiosk,
  schemaId,
  dispatch,
  submit,
  onBack,
}: JobFormScreenProps): JSX.Element {
  const schema = getSchema(schemaId);
  if (schema === undefined) {
    return (
      <section className="fx-screen">
        <div className="banner banner--danger" role="alert">
          <p className="banner__title">No such form</p>
          <p className="banner__body">
            This build has no form with the id <span className="mono">{schemaId}</span>. Nothing has been
            lost; go back and pick again.
          </p>
        </div>
        <button type="button" className="button" onClick={onBack}>
          Back
        </button>
      </section>
    );
  }

  return (
    <section className="fx-screen" aria-label={schema.title}>
      <FormHost
        kiosk={kiosk}
        schema={schema}
        submit={submit}
        intervention={dispatch?.intervention ?? null}
        {...(dispatch !== undefined ? { interventionFetchedAt: dispatch.fetchedAt } : {})}
      />
      <button type="button" className="button" onClick={onBack}>
        Leave this form
      </button>
      <p className="fx-help">
        Leaving loses what you have typed — it is not saved until you submit. A submission, once
        queued, survives closing the app.
      </p>
    </section>
  );
}

/* ------------------------------------------------------------------ queued ---- */

export interface QueuedScreenProps {
  readonly schemaId: string;
  readonly outboxId: string;
  readonly idempotencyKey: string;
  readonly online: boolean;
  readonly onHome: () => void;
  readonly onOpenOutbox: () => void;
}

export function QueuedScreen(props: QueuedScreenProps): JSX.Element {
  const { schemaId, idempotencyKey, online, onHome, onOpenOutbox } = props;
  const schema = getSchema(schemaId);
  return (
    <section className="fx-screen" aria-label="Saved on this phone">
      {/*
        "Saved on this phone", not "Submitted". The submission is durably on disk — `enqueue` resolved
        after the IndexedDB transaction committed — and it has NOT reached the server. Saying "sent"
        here would be the reassuring lie this whole app is built to avoid.
      */}
      <div className="banner banner--ok" role="status">
        <p className="banner__title">Saved on this phone</p>
        <p className="banner__body">
          {schema?.title ?? schemaId} is in the outbox and will send itself when there is a connection.
          You can close the app.
          {online ? '' : ' There is no connection right now, which is fine — nothing is waiting on you.'}
        </p>
      </div>
      {/*
        The key is what support uses to find this exact submission, so it is boxed and one-tap
        selectable rather than set as the smallest dim text on the screen. See `.fx-quote`.
      */}
      <p className="fx-quote">
        <span className="fx-quote__label">Quote this if you report it</span>
        <span className="mono fx-quote__value">{idempotencyKey}</span>
      </p>
      <p className="fx-help">
        That key is how support finds this exact submission. It stays the same on every retry, so a
        retry can never create a second record.
      </p>
      <button type="button" className="fx-big" onClick={onHome}>
        Start another job
      </button>
      <button type="button" className="button" onClick={onOpenOutbox}>
        Open the outbox
      </button>
    </section>
  );
}
